import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import axios from "axios";
import { PrismBanner, PrismSelect } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { apiError } from "./RatingWindowTab";
import { HUB_STATUSES, HUB_TRACKS, type StatusKey, type TrackKey } from "./hub";

interface TrackOverview {
  total: number;
  statuses: Record<StatusKey, number>;
  manager: { pending: number; approved: number; rejected: number };
  seats: number | null;
}

interface RaterProgress {
  user_id: number;
  name: string | null;
  rated: number;
  ratable: number;
}

export interface Overview {
  chapter_id: number;
  year: number;
  years: number[];
  tracks: Record<TrackKey, TrackOverview>;
  ratings: {
    applications: number;
    unrated: number;
    total: number;
    average_per_application: number;
    coverage: number;
    raters: RaterProgress[];
  };
}

const percent = (part: number, whole: number) => (whole > 0 ? Math.min(Math.round((part / whole) * 100), 100) : 0);

type RaterSortKey = "progress" | "name" | "rated";
export interface RaterSort {
  key: RaterSortKey;
  dir: "asc" | "desc";
}

const byName = (a: RaterProgress, b: RaterProgress) => (a.name ?? "").localeCompare(b.name ?? "");

// Default ("progress") puts the slowest raters first; Rater and Rated are sortable by click.
export const sortRaters = (raters: RaterProgress[], sort: RaterSort) => {
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...raters].sort((a, b) => {
    if (sort.key === "name") return sign * byName(a, b);
    if (sort.key === "rated") return sign * (a.rated - b.rated || percent(a.rated, a.ratable) - percent(b.rated, b.ratable)) || byName(a, b);
    return percent(a.rated, a.ratable) - percent(b.rated, b.ratable) || byName(a, b);
  });
};

// Read the Accepted / Seats meter the way the legacy Applications tiles did.
const TrackCard = ({ label, track }: { label: string; track: TrackOverview }) => {
  const accepted = track.statuses.accepted;
  const inPlay = track.total - track.statuses.withdrawn - track.statuses.declined;
  return (
    <section className="hub-track-card" aria-label={label}>
      <header>
        <h3>{label}</h3>
        <span className="hub-track-applied">{inPlay} applied</span>
      </header>
      <div className="hub-track-goal">
        <strong>{accepted}</strong>
        <span>/ {track.seats ?? "—"} seats filled</span>
      </div>
      <div
        className="host-review-seat-bar"
        role="meter"
        aria-label={`${label} seats filled`}
        aria-valuemin={0}
        aria-valuemax={track.seats ?? 0}
        aria-valuenow={accepted}
      >
        <span style={{ width: `${track.seats ? percent(accepted, track.seats) : 0}%` }} />
      </div>
      {track.total > 0 && (
        <div className="hub-status-bar" aria-hidden="true">
          {HUB_STATUSES.filter((s) => track.statuses[s.key] > 0).map((s) => (
            <span
              key={s.key}
              className={`hub-status-${s.key}`}
              style={{ flexGrow: track.statuses[s.key] }}
              title={`${s.label}: ${track.statuses[s.key]}`}
            />
          ))}
        </div>
      )}
      <ul className="hub-status-legend">
        {HUB_STATUSES.filter((s) => track.statuses[s.key] > 0).map((s) => (
          <li key={s.key}>
            <span className={`hub-status-dot hub-status-${s.key}`} aria-hidden="true" />
            {s.label} <strong>{track.statuses[s.key]}</strong>
          </li>
        ))}
        {track.total === 0 && <li className="waitlist-muted">No applications yet</li>}
      </ul>
      <p className="hub-manager-line">
        Manager approval: <strong>{track.manager.approved}</strong> approved · <strong>{track.manager.pending}</strong>{" "}
        waiting
        {track.manager.rejected > 0 && (
          <>
            {" "}
            · <strong>{track.manager.rejected}</strong> declined
          </>
        )}
      </p>
    </section>
  );
};

interface Props {
  chapterId: number;
  year: number;
  onYearChange: (year: number) => void;
}

const OverviewTab = ({ chapterId, year, onYearChange }: Props) => {
  const [data, setData] = useState<Overview | null>(null);
  const [sort, setSort] = useState<RaterSort>({ key: "progress", dir: "asc" });

  const load = useCallback(async () => {
    try {
      const { data: body } = await axios.get<Overview>("/api/applications_hub/overview", {
        params: { chapter_id: chapterId, year },
      });
      setData(body);
    } catch (err) {
      toast.error(apiError(err, "Unable to load the enrollment overview"));
    }
  }, [chapterId, year]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <p>Loading…</p>;

  const unfinished = HUB_TRACKS.reduce((sum, t) => sum + data.tracks[t.key].statuses.not_submitted, 0);
  const { ratings } = data;
  const raters = sortRaters(ratings.raters, sort);
  const toggleSort = (key: RaterSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortHeader = (key: RaterSortKey, label: string) => {
    const active = sort.key === key;
    return (
      <th aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" className="hub-sort" onClick={() => toggleSort(key)}>
          {label}
          <span className="hub-sort-icon" aria-hidden="true">
            {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </button>
      </th>
    );
  };
  const yearOptions = (data.years.length ? data.years : [year]).map((y) => ({ value: String(y), display: String(y) }));

  return (
    <div className="hub-overview">
      <PrismBanner tone="info">
        See how enrollment is going for this chapter: how many people have applied to each track against the seats
        on offer, where their applications stand, and how far each rater has got. Unfinished applications can be
        nudged with an email reminder. The year chosen here also sets the Applications tab.
      </PrismBanner>
      <div className="waitlist-heading">
        <span className="host-review-badge primary">{data.year} cohort</span>
        <PrismSelect
          className="hub-year-select"
          label="Year"
          hideLabel
          value={String(year)}
          options={yearOptions}
          onPrismChange={(e: CustomEvent<{ value: string }>) => onYearChange(Number(e.detail?.value))}
        />
      </div>

      <div className="hub-track-grid">
        {HUB_TRACKS.map((t) => (
          <TrackCard key={t.key} label={t.label} track={data.tracks[t.key]} />
        ))}
      </div>

      {unfinished > 0 && (
        <p className="hub-nudge">
          <strong>{unfinished}</strong> application{unfinished === 1 ? " has" : "s have"} been started but not submitted.{" "}
          <Link to="/admin-settings/email-reminders">Send a reminder</Link>
        </p>
      )}

      <h3>Ratings</h3>
      <dl className="waitlist-seats">
        <div>
          <dt>Applications</dt>
          <dd>{ratings.applications}</dd>
        </div>
        <div>
          <dt>Ratings</dt>
          <dd>{ratings.total}</dd>
        </div>
        <div>
          <dt>Per application</dt>
          <dd>{ratings.average_per_application.toFixed(1)}</dd>
        </div>
        <div>
          <dt>Unrated</dt>
          <dd>{ratings.unrated}</dd>
        </div>
        <div>
          <dt>Coverage</dt>
          <dd>{ratings.coverage}%</dd>
        </div>
      </dl>

      <h3>Rater progress</h3>
      {raters.length === 0 ? (
        <p className="waitlist-empty">Nobody in this chapter can rate applications for {data.year}.</p>
      ) : (
        <table className="application-rater-table waitlist-table hub-rater-table">
          <thead>
            <tr>
              {sortHeader("name", "Rater")}
              {sortHeader("rated", "Rated")}
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {raters.map((r) => (
              <tr key={r.user_id}>
                <td>{r.name ?? "Unknown rater"}</td>
                <td>
                  {r.rated} / {r.ratable}
                </td>
                <td>
                  <div
                    className="host-review-seat-bar"
                    role="meter"
                    aria-label={`${r.name ?? "Rater"} progress`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent(r.rated, r.ratable)}
                  >
                    <span style={{ width: `${percent(r.rated, r.ratable)}%` }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default OverviewTab;
